import {createSelector} from "reselect";
import {type CartGrouping, isPlainLineItem, type LineItem} from "../../models/CartData";
import * as WeightUtils from "../../../utils/WeightUtils";
import * as PriceUtils from "../../../utils/PriceUtils";
import {type RootState} from "../../../redux/store";
import {isKITKITProduct, type ProductData} from "../../../models/ProductData";
import {
  type DeliveryModeData,
  type FreightPaymentsData,
  type SupportedPaymentsTypesData,
} from "../../transformers/CheckoutViewDataTransformer";
import {
  getTotalPriceForLineItem,
  isProductPMAOnCheckout,
} from "../../../feature/ProductDescriptor/v1/productDescriptorUtils";
import {selectDeepProductDescriptor, selectProductDescriptor} from "../../../redux/products/productsSelectors";
import {makeCartGroupingSelector} from "./groupedCartSelectors";

import type {ProductDescriptor} from "~/feature/ProductDescriptor/v1/types";

export const selectPMAProducts = createSelector(
  (state: RootState) => state.cart.cartEntries,
  (state: RootState) => state.products,
  (state: RootState) => state.cart.cartEntriesById,
  (state: RootState) => state.cart.packEntriesById,
  (cartEntryIds, products, cartEntriesById, packEntriesById) => {
    return cartEntryIds.filter((id) => {
      const cartEntry = cartEntriesById[id];
      const isAnyPMA = cartEntry.packEntryIds.some((packId) => {
        const packEntry = packEntriesById[packId];
        return isProductPMAOnCheckout(
          selectDeepProductDescriptor(products, {
            productCode: packEntry.productCode,
            priceReference: packEntry.priceReference,
          }),
        );
      });
      return isAnyPMA;
    });
  },
);
export const selectCartHasPMAProducts = createSelector(
  (state: RootState) => state.cart.cartEntries,
  (state: RootState) => state.products,
  (state: RootState) => state.cart.cartEntriesById,
  selectPMAProducts,
  (cartEntryIds, products, cartEntriesById, selectPMAProducts) => {
    const prod = selectPMAProducts;
    if (prod.length === 0) {
      return false;
    }
    return true;
  },
);

export const selectTotalWeight = createSelector(
  (state: RootState) => state.cart.packEntriesById,
  (packEntriesById) => {
    const packEntries = Object.values(packEntriesById);
    return WeightUtils.sum(...packEntries.map((entry) => WeightUtils.multiply(entry.weight, entry.quantity)));
  },
);

const isOrderable = (packEntry: LineItem, state: RootState) => {
  return state.checkoutPage.orderablePackageIds.includes(packEntry.id);
};

const isQuoteable = (packEntry: LineItem, state: RootState) => {
  return !isOrderable(packEntry, state);
};

export const selectCheckoutCart = makeCartGroupingSelector(isOrderable, isQuoteable);

function isFccFifteenDigits(productDescriptor: ProductDescriptor, packCode: string): boolean {
  return productDescriptor.base.fifteenDigit[packCode]?.isFcc ?? false;
}

const isFcc = (packEntry: LineItem, state: RootState) => {
  const productDescriptor = selectProductDescriptor(state, {
    productCode: packEntry.productCode,
    priceReference: packEntry.priceReference,
  });
  const packCode = packEntry.packCode;
  return isFccFifteenDigits(productDescriptor, packCode);
};

const isNotFcc = (packEntry: LineItem, state: RootState) => {
  return !isFcc(packEntry, state);
};

export const selectSalesmanCheckoutCart = makeCartGroupingSelector(isFcc, isNotFcc);

export const selectAvailableDeliveryModes = (state: RootState, paymentType: string) => {
  return state.checkoutPage.supportedPaymentTypes.find((item) => item.code === paymentType)?.deliveryModes ?? [];
};
export const selectDeliveryModeDetails =
  (deliveryCode: string, paymentType: string) =>
  (state: RootState): DeliveryModeData | undefined =>
    selectAvailableDeliveryModes(state, paymentType).find((item) => item.code === deliveryCode);
export const selectDeliveryModes =
  (paymentType: string) =>
  (state: RootState): SupportedPaymentsTypesData | undefined =>
    state.checkoutPage.supportedPaymentTypes.find((item) => item.code === paymentType);

export const selectSelectedUnloadingPoint = (state: RootState) => state.checkoutPage.unloadingPoint;
export const selectIsSending = (state: RootState) => state.checkoutPage.sending;
export const selectInitialDeliveryMode = (state: RootState) =>
  state.checkoutPage?.supportedPaymentTypes[0]?.deliveryModes[0]?.code ?? "";
export const selectInitialFreightPayment = (state: RootState) =>
  state.checkoutPage?.supportedPaymentTypes[0]?.deliveryModes[0]?.freightPayments
    ? state.checkoutPage?.supportedPaymentTypes[0]?.deliveryModes[0]?.freightPayments[0]?.id
    : "";

export const selectFreightPayment =
  (deliveryCode: string, paymentType: string) =>
  (state: RootState): DeliveryModeData | undefined =>
    selectAvailableDeliveryModes(state, paymentType).find((item) => item.code === deliveryCode);
export const selectAvailableFreightPayments = (state: RootState, deliveryCode: string, paymentType: string) => {
  return (
    selectAvailableDeliveryModes(state, paymentType).find((item) => item.code === deliveryCode)?.freightPayments ?? []
  );
};
export const selectFreightPaymentDetails =
  (deliveryCode: string, paymentType: string, freightPayment: string) =>
  (state: RootState): FreightPaymentsData | undefined =>
    selectAvailableFreightPayments(state, deliveryCode, paymentType).find((item) => item.id === freightPayment);

export const selectTotalCheckoutValue = createSelector(
  selectCheckoutCart,
  (state: RootState) => state.cart.packEntriesById,
  (state: RootState) => state.products.basic,
  (cart, lineItemsById, products) => {
    let lineItems: LineItem[] = [
      ...extractAllLineItemsFromGrouping(cart.orderables, lineItemsById),
      ...cart.bundles.flatMap((bundle) => extractAllLineItemsFromGrouping(bundle, lineItemsById)),
    ];
    lineItems = lineItems.filter(isLineItemValidForCalculatingTotalValue(products));
    return PriceUtils.volatileSum(
      ...lineItems.map((entry) => {
        const product: ProductData | null = products[entry.productCode];
        return getTotalPriceForLineItem(entry, product);
      }),
    );
  },
);

const isLineItemValidForCalculatingTotalValue = (products: Dict<ProductData>) => (lineItem: LineItem) => {
  const product = products[lineItem.productCode];
  return !isKITKITProduct(product);
};

export function extractTopLevelLineItemsFromGrouping(
  cartGrouping: CartGrouping,
  lineItemsById: Dict<LineItem>,
): LineItem[] {
  return cartGrouping.entries
    .flatMap((entry) => entry.packGroups)
    .flatMap((packGroup) => packGroup.packEntryIds)
    .map((id) => lineItemsById[id]);
}

export function extractAllLineItemsFromGrouping(cartGrouping: CartGrouping, lineItemsById: Dict<LineItem>) {
  return extractTopLevelLineItemsFromGrouping(cartGrouping, lineItemsById).flatMap((lineItem) => {
    const result = [lineItem];
    if (isPlainLineItem(lineItem) && lineItem.optionalPartIds) {
      result.push(...lineItem.optionalPartIds.map((optionalId) => lineItemsById[optionalId]));
    }
    return result;
  });
}
